import { convertFromSnakeCase, formatBytes } from '@/_utils/utils';
import useComponentOverride from '@/components/_hooks/useComponentOverride';
import { EllipsisTooltip, PolarisIcon } from '@/exports/columnCommonComponents';
import { IngestFile } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Grid, Typography } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';

export interface MediaInfoPreviewProps {
  data: IngestFile;
  specInput?: Record<string, any>;
}

const FileIngestMediaInfo = ({ data }: MediaInfoPreviewProps) => {
  const getTitle = () => {
    return (
      <TitleWrapper>
        <Typography variant="h3" sx={{ fontSize: '1.6rem' }}>
          Media Info - {data.file_type.charAt(0).toUpperCase() + data.file_type.slice(1)}
        </Typography>
      </TitleWrapper>
    );
  };

  switch (data.file_type) {
    case 'video':
      return (
        <>
          {getTitle()}
          <Grid container spacing={1}>
            {data.media_info?.video &&
              Object.keys(data.media_info.video).map((key) => {
                const value = data.media_info.video[key];
                return (
                  <InfoTile key={key} label={key}>
                    {typeof value === 'boolean' ? (
                      <BooleanValue>
                        <StyledBooleanIcon height={20} icon={value === true ? 'complete_outline' : 'close_outline'} value={+value} />
                        {value === true ? 'True' : 'False'}
                      </BooleanValue>
                    ) : (
                      String(value)
                    )}
                  </InfoTile>
                );
              })}
          </Grid>
        </>
      );
    case 'image':
      return (
        <>
          {getTitle()}
          <Grid container spacing={1}>
            <InfoTile label="file_path">{data.file_path}</InfoTile>
            <InfoTile label="file_size">{formatBytes(data.file_size)}</InfoTile>
            <InfoTile label="filename">{data.filename}</InfoTile>
          </Grid>
        </>
      );
    case 'audio':
      return (
        <>
          {getTitle()}
          <Grid container spacing={1}>
            {data.media_info?.audio_tracks?.map((track: any, index: number) => {
              return (
                <InfoTile key={index} label={`Track ${index + 1}`}>
                  {Object.keys(track).map((key) => (
                    <div key={key}>
                      <span>{convertFromSnakeCase(key)}:</span> {track[key]}
                    </div>
                  ))}
                </InfoTile>
              );
            })}
          </Grid>
        </>
      );
    case 'subtitle':
      return (
        <>
          {getTitle()}
          <Grid container spacing={1}>
            {data.media_info?.caption_tracks?.map((track: any, index: number) => {
              return (
                <InfoTile key={index} label={`Track ${index + 1}`}>
                  {Object.keys(track).map((key) => (
                    <div key={key}>
                      <span>{key}:</span> {track[key]}
                    </div>
                  ))}
                </InfoTile>
              );
            })}
          </Grid>
        </>
      );

    default:
      return (
        <TitleWrapper>
          <Typography variant="h6" sx={{ fontStyle: 'italic', opacity: 0.5 }}>
            No media info available.
          </Typography>
        </TitleWrapper>
      );
  }
};

export default (props: MediaInfoPreviewProps) => useComponentOverride<MediaInfoPreviewProps>(FileIngestMediaInfo, 'fileIngestMediaInfo', props);

const InfoTile = ({ label, children }: { label: string; children: ReactNode }) => {
  const tileRef = useRef<any>(null);
  const [maxWidth, setMaxWidth] = useState(200);

  useEffect(() => {
    if (tileRef.current) {
      setMaxWidth(tileRef.current.clientWidth - tileRef.current.children[0].clientWidth - 32);
    }
  }, [tileRef.current]);

  return (
    <Grid item xs={6}>
      <TileWrapper ref={tileRef} container>
        <span>{convertFromSnakeCase(label)}</span>
        <span>
          {!tileRef.current ? (
            <PolarisIcon icon="loading_dots" />
          ) : typeof children === 'string' && children?.length > 0 ? (
            <EllipsisTooltip text={children} maxWidth={maxWidth} />
          ) : (
            children || '-'
          )}
        </span>
      </TileWrapper>
    </Grid>
  );
};

const TitleWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const TileWrapper = styled(Grid)`
  background: ${({ theme }) => theme.palette.background.paper};
  min-height: 44px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(1, 2)};
  height: 100%;

  > span:nth-of-type(1) {
    font-size: 1.1rem;
    opacity: 0.5;
    padding-right: ${({ theme }) => theme.spacing(2)};
  }

  > span:nth-of-type(2) {
    font-size: 1.1rem;
    opacity: 0.8;
  }
`;

const StyledBooleanIcon = styled(PolarisIcon)<{ value: number }>`
  opacity: ${({ value, theme }) => (value ? 1 : 0.3)};
`;

const BooleanValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;
